/**
 * Nutrition Quick Actions
 * Action buttons and water tracker for trainer dashboard
 */

import { Droplets, History, Settings2, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useDailySummary } from '@/hooks/use-nutrition-summary';
import { DEFAULT_GOALS, WATER_INCREMENTS } from '@/lib/nutrition';

interface NutritionQuickActionsProps {
  clientId: string;
  onConfigureClick?: () => void;
  onViewHistoryClick?: () => void;
  onViewTrendsClick?: () => void;
}

export function NutritionQuickActions({
  clientId,
  onConfigureClick,
  onViewHistoryClick,
  onViewTrendsClick,
}: NutritionQuickActionsProps) {
  const { data: summary } = useDailySummary({ clientId });

  const waterCurrent = summary?.total_water_oz ?? 0;
  const waterTarget = summary?.goal?.target_water_oz ?? DEFAULT_GOALS.water_oz;
  const waterPercentage = Math.min((waterCurrent / waterTarget) * 100, 100);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Droplets className="h-4 w-4 text-info" />
          Quick Actions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Water Tracker */}
        <div className="p-3 rounded-lg bg-gradient-to-r from-info/10 to-info/5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium">Water Intake</span>
            <span className="text-xs tabular-nums text-muted-foreground">
              {waterCurrent} / {waterTarget} oz
            </span>
          </div>
          <Progress
            value={waterPercentage}
            className="h-2"
          />
          <div className="flex items-center justify-between mt-2">
            <div className="flex gap-1">
              {[8, 16, 32].map((oz) => (
                <Button
                  key={oz}
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-[10px]"
                  disabled
                  title="Client logs water in portal"
                >
                  +{oz}oz
                </Button>
              ))}
            </div>
            <span className={cn(
              'text-xs font-medium',
              waterPercentage >= 100 ? 'text-success' :
              waterPercentage >= 75 ? 'text-info' :
              'text-muted-foreground'
            )}>
              {waterPercentage.toFixed(0)}%
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 gap-2">
          {onConfigureClick && (
            <Button
              variant="outline"
              size="sm"
              className="justify-start gap-2"
              onClick={onConfigureClick}
            >
              <Settings2 className="h-4 w-4" />
              Configure Macro Plan
            </Button>
          )}

          {onViewHistoryClick && (
            <Button
              variant="outline"
              size="sm"
              className="justify-start gap-2"
              onClick={onViewHistoryClick}
            >
              <History className="h-4 w-4" />
              View Full History
            </Button>
          )}

          {onViewTrendsClick && (
            <Button
              variant="outline"
              size="sm"
              className="justify-start gap-2"
              onClick={onViewTrendsClick}
            >
              <TrendingUp className="h-4 w-4" />
              View Trends & Insights
            </Button>
          )}
        </div>

        {/* Info */}
        <p className="text-[10px] text-muted-foreground text-center pt-2 border-t">
          Clients log meals and water via the client portal
        </p>
      </CardContent>
    </Card>
  );
}
